// Remove mock data import and replace with proper interfaces
export interface Client {
  _id?: string;
  name: string;
  email: string;
  phone: string;
  age: number;
  gender: "male" | "female" | "other";
  goal: string;
  image: string;
  height?: number;
  weight?: number;
  dietType?: string;
  allergies?: string[];
  foodPreferences?: string;
  profession?: string;
  hasWhatsApp?: boolean;
  visitCount?: {
    total: number;
    thisMonth: number;
    thisWeek: number;
  };
  outstandingBalance?: number;
  subscription?: {
    startDate?: string | Date;
    endDate?: string | Date;
    note?: string;
  };
  healthNotes?: Array<{
    title: string;
    content: string;
    categories: string[];
    createdAt: string;
  }>;
  observations?: Array<{
    date: string;
    notes: string;
    createdAt: string;
  }>;
  measurements?: Array<{
    date: string;
    weight: number;
    bodyFat?: number;
    chest?: number;
    waist?: number;
    hips?: number;
    arms?: number;
    thighs?: number;
    createdAt: string;
  }>;
  inBody?: Array<{
    date: string;
    weight: number;
    skeletalMuscle: number;
    bodyFat: number;
    bmi: number;
    bodyFatMass: number;
    pbf: number; // Percent Body Fat
    minerals: number;
    protein: number;
    notes?: string;
    fileUrl?: string;
    createdAt: string;
  }>;
  diet?: Array<{
    date: string;
    name: string;
    type: string;
    weight: number;
    height: number;
    age: number;
    gender: string;
    equationMethod: string;
    bodyFat?: number;
    factor1?: number;
    factor2?: number;
    factor3?: number;
    factor4?: number;
    bmr: number;
    activityLevel: number;
    calories: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    notes?: string;
    createdAt: string;
    updatedAt?: string;
  }>;
  medicalInfo?: {
    conditions: string[];
    medications: string[];
    allergies: string[];
    bloodType?: string;
    reassessmentDate?: string;
    notes?: string;
    lastUpdated: string;
  };
  gallery?: Array<{
    url: string;
    date: Date;
    notes?: string;
    createdAt: Date;
  }>;
  dietReviewedAt?: string | Date | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Meeting {
  _id?: string;
  client: string;
  title: string;
  startTime: Date | string;
  endTime: Date | string;
  type: "initial" | "follow-up" | "assessment";
  notes?: string;
  status?: "scheduled" | "completed" | "cancelled" | "no-show";
  clientName?: string; // Added for frontend display purposes
}

export interface FoodItem {
  _id?: string;
  name: string;
  energy: number;
  protein: number;
  carbohydrate: number;
  fat: number;
  category: string;
  servingSize: string;
  description?: string;
  isCustom?: boolean;
  // Additional nutritional fields
  refuse?: number;
  water?: number;
  ash?: number;
  fiber?: number;
  sodium?: number;
  potassium?: number;
  calcium?: number;
  phosphorus?: number;
  magnesium?: number;
  iron?: number;
  zinc?: number;
  copper?: number;
  vitaminA?: number;
  vitaminC?: number;
  thiamin?: number;
  riboflavin?: number;
}

export interface Exercise {
  _id?: string;
  name: string;
  category: string;
  targetMuscleGroup: string;
  equipment: string[];
  difficulty: "beginner" | "intermediate" | "advanced";
  instructions: string;
}

export interface FinancialRecord {
  _id?: string;
  date: Date | string;
  type: "income" | "expense";
  amount: number;
  description: string;
  category: string;
  client?: string;
  clientName?: string;
  paymentMethod?: string;
  status?: string;
  totalAmount?: number; // Total amount owed by client
  remainingBalance?: number; // Remaining balance after this payment
}

export interface LandingSectionOne {
  _id?: string;
  title: string;
  subtitle: string;
  mainImage: string;
  description: string;
}

export interface ProductSection {
  _id?: string;
  sectionTitle: string;
  sectionSubtitle: string;
  products: Array<{
    badge: string;
    img: string;
    title: string;
    rating: number;
    reviews: number;
    description: string;
    price: string;
  }>;
}

export interface IntelligentDesignSection {
  _id?: string;
  heading: string;
  description: string;
  techLogos: Array<{
    text: string;
  }>;
  imageUrl: string;
}

export interface FeaturesSection {
  _id?: string;
  heading: string;
  subtitle: string;
  features: Array<{
    iconUrl: string;
    title: string;
    description: string;
  }>;
  ctaButton: string;
  ratingLabel: string;
}

export interface Testimonial {
  photo: string;
  name: string;
  role: string;
  title: string;
  quote: string;
}

export interface TestimonialsSection {
  _id?: string;
  heading: string;
  ratingLabel: string;
  testimonials: Testimonial[];
}

export interface WhoSectionItem {
  iconUrl: string;
  title: string;
  description: string;
}

export interface WhoSection {
  _id?: string;
  heading: string;
  subtitle: string;
  items: WhoSectionItem[];
  ctaButton: string;
  ratingLabel: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqSection {
  _id?: string;
  heading: string;
  subtitle: string;
  faqs: FaqItem[];
  ratingLabel: string;
}

export interface PricingPlan {
  name: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  isPopular: boolean;
  popularBadge: string;
}

export interface PricingSection {
  _id?: string;
  heading: string;
  subtitle: string;
  plans: PricingPlan[];
}

export interface DietPlanFood {
  name: string;
  category?: string;
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
  sodium?: number;
  potassium?: number;
  calcium?: number;
  iron?: number;
  quantity: number;
  unit?: string;
  notes?: string;
}

export interface DietPlanMeal {
  name: string;
  foods: DietPlanFood[];
  notes?: string;
}

// A `day` groups meals for a single day inside a plan. New plans always save
// into `days`; old plans saved with the legacy flat `meals` field are auto-
// wrapped client-side into a single Day 1.
//
// `isLinked` means "this day mirrors the template (days[0])". The first day is
// always the template; other days default to linked so editing the template
// propagates everywhere until that day is explicitly customized.
export interface DietPlanDay {
  name: string;
  meals: DietPlanMeal[];
  isLinked?: boolean;
}

export interface DietPlan {
  _id?: string;
  client: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  dailyCalorieTarget?: number;
  macroTargets?: {
    protein?: number;
    carbs?: number;
    fat?: number;
  };
  days?: DietPlanDay[];
  meals?: DietPlanMeal[];
  specialInstructions?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Check if we're running in Electron
const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');

// Get API URL based on environment
const API_URL = '/api';

/** Turn stored upload URLs (absolute http host, /uploads, or /api/uploads) into a same-origin path. */
export const resolveUploadUrl = (url?: string): string => {
  if (!url) return '';
  const uploadsIndex = url.indexOf('/uploads/');
  if (uploadsIndex !== -1) {
    const filename = url.slice(uploadsIndex + '/uploads/'.length);
    return `/api/uploads/${filename}`;
  }
  if (url.startsWith('/api/')) return url;
  if (url.startsWith('/')) return url;
  return `/api/uploads/${url}`;
};

export const isPdfUrl = (url?: string): boolean => {
  const pathOnly = (url || '').split('?')[0].toLowerCase();
  return pathOnly.endsWith('.pdf');
};

// Token storage helpers (admin/doctor auth). The token is stored in
// localStorage; never the password itself.
const ADMIN_TOKEN_KEY = 'doctorAuthToken';

export const getAdminToken = (): string | null => {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setAdminToken = (token: string | null) => {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore storage errors */
  }
};

const SENSITIVE_ENDPOINT_PATTERNS = [
  '/auth/login',
  '/auth/change-password',
  '/client-accounts',
];

const isSensitiveEndpoint = (endpoint: string) =>
  SENSITIVE_ENDPOINT_PATTERNS.some(p => endpoint.includes(p));

// Generic API request function with error handling
async function apiRequest<T>(
  endpoint: string,
  method: string = 'GET',
  data?: any
): Promise<T> {
  const url = `${API_URL}${endpoint}`;
  const token = getAdminToken();

  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(isElectron && {
        'Origin': 'http://localhost:8080',
        'Accept': 'application/json'
      })
    },
    ...(data && { body: JSON.stringify(data) }),
  };

  try {
    if (!isSensitiveEndpoint(endpoint)) {
      console.log(`Making ${method} request to ${url}`);
    }
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API request failed:', {
        status: response.status,
        statusText: response.statusText,
        message: errorData?.message,
      });
      if (response.status === 401 && token) {
        setAdminToken(null);
      }
      throw new Error(errorData.message || `API request failed with status ${response.status}`);
    }

    const responseData = await response.json();
    if (!isSensitiveEndpoint(endpoint)) {
      console.log(`Successful ${method} response from ${url}`);
    }
    return responseData;
  } catch (error) {
    console.error('API request failed:', error instanceof Error ? error.message : error);
    throw error;
  }
}

// Client API
export const clientApi = {
  getAll: async (): Promise<Client[]> => {
    return apiRequest<Client[]>('/clients');
  },
  
  getById: async (id: string): Promise<Client> => {
    return apiRequest<Client>(`/clients/${id}`);
  },
  
  create: async (client: Omit<Client, '_id'>): Promise<Client> => {
    return apiRequest<Client>('/clients', 'POST', client);
  },
  
  update: async (id: string, client: Partial<Client>): Promise<Client> => {
    return apiRequest<Client>(`/clients/${id}`, 'PUT', client);
  },
  
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/clients/${id}`, 'DELETE');
  },
  
  search: async (query: string): Promise<Client[]> => {
    return apiRequest<Client[]>(`/clients/search?query=${encodeURIComponent(query)}`);
  }
};

// Meeting API
export const meetingApi = {
  getAll: async (params?: { start?: Date; end?: Date; client?: string }): Promise<Meeting[]> => {
    let queryString = '';
    
    if (params) {
      const queryParams = new URLSearchParams();
      
      if (params.start) {
        queryParams.append('start', params.start.toISOString());
      }
      
      if (params.end) {
        queryParams.append('end', params.end.toISOString());
      }
      
      if (params.client) {
        queryParams.append('client', params.client);
      }
      
      queryString = `?${queryParams.toString()}`;
    }
    
    return apiRequest<Meeting[]>(`/meetings${queryString}`);
  },
  
  getById: async (id: string): Promise<Meeting> => {
    return apiRequest<Meeting>(`/meetings/${id}`);
  },
  
  create: async (meeting: Omit<Meeting, '_id'>): Promise<Meeting> => {
    return apiRequest<Meeting>('/meetings', 'POST', meeting);
  },
  
  update: async (id: string, meeting: Partial<Meeting>): Promise<Meeting> => {
    return apiRequest<Meeting>(`/meetings/${id}`, 'PUT', meeting);
  },
  
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/meetings/${id}`, 'DELETE');
  },
  
  updateStatus: async (id: string, status: string): Promise<Meeting> => {
    return apiRequest<Meeting>(`/meetings/${id}/status`, 'PATCH', { status });
  }
};

// Food Item API
export const foodItemApi = {
  getAll: async (): Promise<FoodItem[]> => {
    return apiRequest<FoodItem[]>('/food-items');
  },
  
  getById: async (id: string): Promise<FoodItem> => {
    return apiRequest<FoodItem>(`/food-items/${id}`);
  },
  
  create: async (foodItem: Omit<FoodItem, '_id'>): Promise<FoodItem> => {
    return apiRequest<FoodItem>('/food-items', 'POST', foodItem);
  },
  
  update: async (id: string, foodItem: Partial<FoodItem>): Promise<FoodItem> => {
    return apiRequest<FoodItem>(`/food-items/${id}`, 'PUT', foodItem);
  },
  
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/food-items/${id}`, 'DELETE');
  }
};

// Exercise API
export const exerciseApi = {
  getAll: async (): Promise<Exercise[]> => {
    return apiRequest<Exercise[]>('/exercises');
  },
  
  getById: async (id: string): Promise<Exercise> => {
    return apiRequest<Exercise>(`/exercises/${id}`);
  },
  
  create: async (exercise: Omit<Exercise, '_id'>): Promise<Exercise> => {
    return apiRequest<Exercise>('/exercises', 'POST', exercise);
  },
  
  update: async (id: string, exercise: Partial<Exercise>): Promise<Exercise> => {
    return apiRequest<Exercise>(`/exercises/${id}`, 'PUT', exercise);
  },
  
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/exercises/${id}`, 'DELETE');
  }
};

// Financial Record API
export const financialApi = {
  getAll: async (): Promise<FinancialRecord[]> => {
    return apiRequest<FinancialRecord[]>('/financial-records');
  },
  
  getById: async (id: string): Promise<FinancialRecord> => {
    return apiRequest<FinancialRecord>(`/financial-records/${id}`);
  },
  
  create: async (record: Omit<FinancialRecord, '_id'>): Promise<FinancialRecord> => {
    return apiRequest<FinancialRecord>('/financial-records', 'POST', record);
  },
  
  update: async (id: string, record: Partial<FinancialRecord>): Promise<FinancialRecord> => {
    return apiRequest<FinancialRecord>(`/financial-records/${id}`, 'PUT', record);
  },
  
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/financial-records/${id}`, 'DELETE');
  },
  
  getSummary: async (startDate: Date, endDate: Date): Promise<any> => {
    return apiRequest<any>(
      `/financial-records/summary?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
    );
  }
};

// Dashboard metrics API
export const metricsApi = {
  getBusinessMetrics: async (): Promise<any> => {
    return apiRequest<any>('/metrics/business');
  }
};

export const landingSectionOneApi = {
  get: async (): Promise<LandingSectionOne> => {
    return apiRequest<LandingSectionOne>('/landing-section-one');
  },
  update: async (data: Omit<LandingSectionOne, '_id'>): Promise<LandingSectionOne> => {
    return apiRequest<LandingSectionOne>('/landing-section-one', 'PUT', data);
  }
};

export const productSectionApi = {
  get: async (): Promise<ProductSection> => {
    return apiRequest<ProductSection>('/product-section');
  },
  update: async (data: Omit<ProductSection, '_id'>): Promise<ProductSection> => {
    return apiRequest<ProductSection>('/product-section', 'PUT', data);
  }
};

export const intelligentDesignSectionApi = {
  get: async (): Promise<IntelligentDesignSection> => {
    return apiRequest<IntelligentDesignSection>('/intelligent-design-section');
  },
  update: async (data: Omit<IntelligentDesignSection, '_id'>): Promise<IntelligentDesignSection> => {
    return apiRequest<IntelligentDesignSection>('/intelligent-design-section', 'PUT', data);
  }
};

export const featuresSectionApi = {
  get: async (): Promise<FeaturesSection> => {
    return apiRequest<FeaturesSection>('/features-section');
  },
  update: async (data: Omit<FeaturesSection, '_id'>): Promise<FeaturesSection> => {
    return apiRequest<FeaturesSection>('/features-section', 'PUT', data);
  }
};

export const testimonialsSectionApi = {
  get: async (): Promise<TestimonialsSection> => {
    return apiRequest<TestimonialsSection>('/testimonials-section');
  },
  update: async (data: Omit<TestimonialsSection, '_id'>): Promise<TestimonialsSection> => {
    return apiRequest<TestimonialsSection>('/testimonials-section', 'PUT', data);
  }
};

export const whoSectionApi = {
  get: async (): Promise<WhoSection> => {
    return apiRequest<WhoSection>('/who-section');
  },
  update: async (data: Omit<WhoSection, '_id'>): Promise<WhoSection> => {
    return apiRequest<WhoSection>('/who-section', 'PUT', data);
  }
};

export const faqSectionApi = {
  get: async (): Promise<FaqSection> => {
    return apiRequest<FaqSection>('/faq-section');
  },
  update: async (data: Omit<FaqSection, '_id'>): Promise<FaqSection> => {
    return apiRequest<FaqSection>('/faq-section', 'PUT', data);
  }
};

export const pricingSectionApi = {
  get: async (): Promise<PricingSection> => {
    return apiRequest<PricingSection>('/pricing-section');
  },
  update: async (data: Omit<PricingSection, '_id'>): Promise<PricingSection> => {
    return apiRequest<PricingSection>('/pricing-section', 'PUT', data);
  }
};

export const dietPlanApi = {
  create: async (plan: Omit<DietPlan, '_id'>): Promise<DietPlan> => {
    return apiRequest<DietPlan>('/diet-plans', 'POST', plan);
  },
  getAll: async (clientId?: string): Promise<DietPlan[]> => {
    const query = clientId ? `?client=${clientId}` : '';
    return apiRequest<DietPlan[]>(`/diet-plans${query}`);
  },
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/diet-plans/${id}`, 'DELETE');
  },
  update: async (id: string, plan: Omit<DietPlan, '_id'>): Promise<DietPlan> => {
    return apiRequest<DietPlan>(`/diet-plans/${id}`, 'PUT', plan);
  }
};

// --- Workout Plan Types ---
export interface WorkoutExerciseEntry {
  exercise: string; // Exercise ObjectId
  sets: number;
  reps: number;
  weight?: number;
  duration?: number;
  restTime?: number;
  notes?: string;
}

export interface WorkoutSession {
  name: string;
  targetDay?: string; // e.g. 'monday', 'tuesday', ...
  exercises: WorkoutExerciseEntry[];
  warmup?: string;
  cooldown?: string;
  duration?: number;
  notes?: string;
}

export interface WorkoutPlan {
  _id?: string;
  client: string;
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  sessionsPerWeek?: number;
  workoutSessions: WorkoutSession[];
  goals?: string;
  intensity?: 'low' | 'moderate' | 'high' | 'extreme';
  status?: 'active' | 'completed' | 'draft';
  progression?: Array<{
    date: string;
    notes?: string;
    achievements?: string[];
  }>;
  specialInstructions?: string;
  createdAt?: string;
  updatedAt?: string;
}

// --- Workout Plan API ---
export const workoutPlanApi = {
  create: async (plan: Omit<WorkoutPlan, '_id'>): Promise<WorkoutPlan> => {
    return apiRequest<WorkoutPlan>('/workout-plans', 'POST', plan);
  },
  getAll: async (clientId?: string): Promise<WorkoutPlan[]> => {
    const query = clientId ? `?client=${clientId}` : '';
    return apiRequest<WorkoutPlan[]>(`/workout-plans${query}`);
  },
  getById: async (id: string): Promise<WorkoutPlan> => {
    return apiRequest<WorkoutPlan>(`/workout-plans/${id}`);
  },
  update: async (id: string, plan: Partial<WorkoutPlan>): Promise<WorkoutPlan> => {
    return apiRequest<WorkoutPlan>(`/workout-plans/${id}`, 'PUT', plan);
  },
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/workout-plans/${id}`, 'DELETE');
  },
};

// --- Client-Workout Association API ---
export const clientWorkoutApi = {
  addWorkout: async (clientId: string, workoutId: string): Promise<Client> => {
    return apiRequest<Client>(`/clients/${clientId}/workouts`, 'POST', { workoutId });
  },
  removeWorkout: async (clientId: string, workoutId: string): Promise<Client> => {
    return apiRequest<Client>(`/clients/${clientId}/workouts/${workoutId}`, 'DELETE');
  },
};

// --- Client Accounts (client login credentials) ---
export interface ClientAccount {
  _id?: string;
  client: string | { _id: string; name: string; email: string };
  email: string;
}

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'doctor' | 'nutritionist' | 'assistant' | 'staff';
  active?: boolean;
  lastLogin?: string;
}

export const adminAuthApi = {
  login: async (data: { email: string; password: string }): Promise<{ token: string; user: AdminUser }> => {
    const result = await apiRequest<{ token: string; user: AdminUser }>('/auth/login', 'POST', data);
    if (result?.token) setAdminToken(result.token);
    return result;
  },
  me: async (): Promise<{ user: AdminUser }> => {
    return apiRequest<{ user: AdminUser }>('/auth/me');
  },
  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>('/auth/change-password', 'POST', data);
  },
  logout: () => {
    setAdminToken(null);
  },
};

export const clientAccountApi = {
  getAll: async (): Promise<ClientAccount[]> => {
    return apiRequest<ClientAccount[]>('/client-accounts');
  },
  create: async (data: { clientId: string; email: string; password: string }): Promise<ClientAccount> => {
    return apiRequest<ClientAccount>('/client-accounts', 'POST', data);
  },
  update: async (id: string, data: { email?: string; password?: string }): Promise<ClientAccount> => {
    return apiRequest<ClientAccount>(`/client-accounts/${id}`, 'PUT', data);
  },
  delete: async (id: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/client-accounts/${id}`, 'DELETE');
  },
  login: async (data: { email: string; password: string }): Promise<ClientAccount> => {
    return apiRequest<ClientAccount>('/client-accounts/login', 'POST', data);
  },
  getPassword: async (id: string): Promise<{ password: string }> => {
    return apiRequest<{ password: string }>(`/client-accounts/${id}/password`);
  },
};

export interface WhatAreYouWaitingForSection {
  headline: string;
  description: string;
  ctaText: string;
  ratingLabel: string;
}

export const waitingSectionApi = {
  get: async (): Promise<WhatAreYouWaitingForSection> => {
    const res = await fetch('/api/waiting-section');
    if (!res.ok) throw new Error('Failed to fetch waiting section');
    return res.json();
  },
  update: async (data: WhatAreYouWaitingForSection): Promise<WhatAreYouWaitingForSection> => {
    const res = await fetch('/api/waiting-section', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update waiting section');
    return res.json();
  },
};

export interface Footer {
  brand: {
    logoText: string;
    disclaimer: string;
    socialLinks: Array<{
      platform: string;
      url: string;
      icon: string;
    }>;
  };
  quickLinks: Array<{
    text: string;
    url: string;
  }>;
  customerService: Array<{
    text: string;
    url: string;
  }>;
  paymentMethods: Array<{
    name: string;
    icon: string;
  }>;
}

// Exam Interfaces
export interface ExamQuestion {
  _id?: string;
  question: string;
  order: number;
}

export interface ExamAnswer {
  questionId: string;
  answer: string;
  answeredAt?: Date;
}

export interface ExamAssignment {
  _id?: string;
  client: string | Client;
  assignedAt: Date;
  dueDate?: Date;
  status: 'assigned' | 'in_progress' | 'completed' | 'overdue';
  answers: ExamAnswer[];
  submittedAt?: Date;
  score?: number;
  feedback?: string;
}

export interface Exam {
  _id?: string;
  title: string;
  description?: string;
  questions: ExamQuestion[];
  createdBy?: string | { name: string; email: string };
  isTemplate: boolean;
  // For client-specific exams
  assignedTo?: string | { _id: string; name: string; email: string };
  assignedAt?: Date;
  dueDate?: Date;
  status?: 'assigned' | 'in_progress' | 'completed' | 'overdue';
  answers?: ExamAnswer[];
  submittedAt?: Date;
  score?: number;
  feedback?: string;
  originalTemplate?: string | { title: string };
  // Legacy support for old structure
  assignments?: ExamAssignment[];
  category?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  estimatedTime?: number;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ExamStats {
  totalAssignments: number;
  completedAssignments: number;
  inProgressAssignments: number;
  overdueAssignments: number;
  completionRate: number;
}

export const footerApi = {
  get: async (): Promise<Footer> => {
    const res = await fetch('/api/footer');
    if (!res.ok) throw new Error('Failed to fetch footer');
    return res.json();
  },
  update: async (data: Footer): Promise<Footer> => {
    const res = await fetch('/api/footer', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update footer');
    return res.json();
  },
};

// Exam API functions
export const examApi = {
  // Get all exams (templates and assignments)
  getAllExams: async (): Promise<Exam[]> => {
    const res = await fetch('/api/exams');
    if (!res.ok) throw new Error('Failed to fetch exams');
    return res.json();
  },
  
  // Get exam templates only
  getExamTemplates: async (): Promise<Exam[]> => {
    const res = await fetch('/api/exams/templates');
    if (!res.ok) throw new Error('Failed to fetch exam templates');
    return res.json();
  },
  
  // Get exam by ID
  getExamById: async (id: string): Promise<Exam> => {
    const res = await fetch(`/api/exams/${id}`);
    if (!res.ok) throw new Error('Failed to fetch exam');
    return res.json();
  },
  
  // Create new exam template
  createExamTemplate: async (data: {
    title: string;
    description?: string;
    questions: string[];
    category?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    estimatedTime?: number;
  }): Promise<Exam> => {
    const res = await fetch('/api/exams', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create exam template');
    return res.json();
  },
  
  // Update exam template
  updateExamTemplate: async (id: string, data: {
    title: string;
    description?: string;
    questions: string[];
    category?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    estimatedTime?: number;
  }): Promise<Exam> => {
    const res = await fetch(`/api/exams/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update exam template');
    return res.json();
  },
  
  // Delete exam template
  deleteExamTemplate: async (id: string): Promise<{ message: string }> => {
    const res = await fetch(`/api/exams/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete exam template');
    return res.json();
  },
  
  // Assign exam to clients
  assignExamToClients: async (examId: string, data: {
    clientIds: string[];
    dueDate?: string;
  }): Promise<Exam> => {
    const res = await fetch(`/api/exams/${examId}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to assign exam to clients (${res.status})`);
    }
    
    return res.json();
  },
  
  // Get client's assigned exams
  getClientExams: async (clientId: string): Promise<Exam[]> => {
    const res = await fetch(`/api/exams/client/${clientId}`);
    if (!res.ok) throw new Error('Failed to fetch client exams');
    return res.json();
  },
  
  // Get client's assigned exams by name
  getClientExamsByName: async (clientName: string): Promise<Exam[]> => {
    const res = await fetch(`/api/exams/client/name/${encodeURIComponent(clientName)}`);
    if (!res.ok) throw new Error('Failed to fetch client exams');
    return res.json();
  },
  
  // Submit exam answers
  submitExamAnswers: async (examId: string, clientId: string, data: {
    answers: ExamAnswer[];
  }): Promise<Exam> => {
    const res = await fetch(`/api/exams/${examId}/client/${clientId}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to submit exam answers');
    return res.json();
  },
  
  // Get exam statistics
  getExamStats: async (examId: string): Promise<ExamStats> => {
    const res = await fetch(`/api/exams/${examId}/stats`);
    if (!res.ok) throw new Error('Failed to fetch exam statistics');
    return res.json();
  },
  
  // Get completed exams
  getCompletedExams: async (): Promise<Exam[]> => {
    const res = await fetch('/api/exams/completed');
    if (!res.ok) throw new Error('Failed to fetch completed exams');
    return res.json();
  },
  
  // Get all assigned exams (both completed and pending)
  getAllAssignedExams: async (): Promise<Exam[]> => {
    const res = await fetch('/api/exams/assigned');
    if (!res.ok) throw new Error('Failed to fetch assigned exams');
    return res.json();
  },
};

// Notification interface
export interface Notification {
  _id?: string;
  recipient: 'doctor' | 'client';
  recipientId: string | { _id: string; name: string; email: string };
  type: 'meeting_assigned' | 'exam_assigned' | 'exam_completed' | 'workout' | 'meal' | 'achievement';
  title: string;
  message: string;
  relatedItem?: {
    type: 'meeting' | 'exam' | 'workout' | 'meal';
    id: string;
  };
  read: boolean;
  priority: 'low' | 'medium' | 'high';
  createdAt: Date | string;
  updatedAt?: Date | string;
}

// Notification API
export const notificationApi = {
  // Get notifications for a client
  getClientNotifications: async (clientName: string, params?: { limit?: number; unreadOnly?: boolean }): Promise<Notification[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.unreadOnly) queryParams.append('unreadOnly', params.unreadOnly.toString());
    
    return apiRequest<Notification[]>(`/notifications/client/${encodeURIComponent(clientName)}?${queryParams.toString()}`);
  },
  
  // Get notifications for doctor
  getDoctorNotifications: async (params?: { limit?: number; unreadOnly?: boolean }): Promise<Notification[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.unreadOnly) queryParams.append('unreadOnly', params.unreadOnly.toString());
    
    return apiRequest<Notification[]>(`/notifications/doctor?${queryParams.toString()}`);
  },
  
  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<Notification> => {
    return apiRequest<Notification>(`/notifications/${notificationId}/read`, 'PATCH');
  },
  
  // Mark all notifications as read
  markAllAsRead: async (recipient: 'doctor' | 'client', recipientId?: string, clientName?: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>('/notifications/read-all', 'PATCH', { recipient, recipientId, clientName });
  },
  
  // Delete notification
  deleteNotification: async (notificationId: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`/notifications/${notificationId}`, 'DELETE');
  }
};